import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { DASHBOARD } from "../constants/testIds";
import { Sparkles, Navigation2 } from "lucide-react";
import NavigationDialog from "./NavigationDialog";
import { formatPrice, formatPricePerM2 } from "../utils/priceFormat";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  Gezdiklerim: "#10B981",
  Gezeceklerim: "#3B82F6",
  "Sahip Olduklarım": "#F59E0B",
};

const verdictConfig = {
  firsat: {
    label: "FIRSAT",
    color: "#10B981",
    bg: "rgba(16, 185, 129, 0.1)",
  },
  orta: {
    label: "ORTA",
    color: "#3B82F6",
    bg: "rgba(59, 130, 246, 0.1)",
  },
  riskli: {
    label: "RİSKLİ",
    color: "#EF4444",
    bg: "rgba(239, 68, 68, 0.1)",
  },
  yetersiz_veri: {
    label: "VERİ AZ",
    color: "#6B7280",
    bg: "rgba(107, 114, 128, 0.1)",
  },
};

const createCustomIcon = (color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="background-color: ${color}; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); border: 3px solid white;"><div style="width: 10px; height: 10px; background-color: white; border-radius: 50%;"></div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

const MapUpdater = ({ properties, showAllTrigger }) => {
  const map = useMap();

  useEffect(() => {
    if (showAllTrigger > 0 && properties.length > 0) {
      const pts = properties.filter(p => p.lat && p.lng);
      if (pts.length > 0) {
        const bounds = L.latLngBounds(pts.map((p) => [p.lat, p.lng]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      }
    }
  }, [showAllTrigger, properties, map]);

  return null;
};

const PropertyMarker = ({ property, onPropertyClick, onOpenNav }) => {
  const [summary, setSummary] = useState(property.ai_summary || null);
  const [loading, setLoading] = useState(false);
  const requestedRef = useRef(false);

  const fetchSummary = async () => {
    if (summary || loading || requestedRef.current) return;
    requestedRef.current = true;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/ai/quick-summary/${property.id}`,
        {},
        { withCredentials: true }
      );
      setSummary(data);
    } catch (error) {
      console.error("Quick summary error:", error);
      requestedRef.current = false; // allow retry
    } finally {
      setLoading(false);
    }
  };

  const verdict = summary?.verdict ? verdictConfig[summary.verdict] || verdictConfig.orta : null;

  return (
    <Marker
      position={[property.lat, property.lng]}
      icon={createCustomIcon(categoryColors[property.category] || "#9CA3AF")}
      eventHandlers={{
        click: () => onPropertyClick(property),
        mouseover: () => fetchSummary(),
      }}
    >
      <Popup>
        <div style={{ padding: "4px", minWidth: "180px" }}>
          <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "4px" }}>{property.title}</div>
          {property.location && <div style={{ fontSize: "11px", color: "#666", marginBottom: "8px" }}>{property.location}</div>}
          <button
            onClick={(e) => { e.stopPropagation(); onOpenNav(property); }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              width: "100%", padding: "8px", background: "#3B82F6", color: "white",
              border: "none", borderRadius: "6px", fontWeight: 700, fontSize: "12px", cursor: "pointer"
            }}
          >
            🧭 Yol Tarifi
          </button>
        </div>
      </Popup>
      <Tooltip
        direction="top"
        offset={[0, -18]}
        opacity={1}
        className="ai-hover-tooltip"
      >
        <div style={{ minWidth: "240px", maxWidth: "280px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "6px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: categoryColors[property.category] || "#9CA3AF",
              }}
            />
            <span
              style={{
                fontSize: "10px",
                textTransform: "uppercase",
                letterSpacing: "1px",
                color: "#6B7280",
                fontWeight: 700,
              }}
            >
              {property.category}
            </span>
          </div>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#0A0A0A",
              marginBottom: "2px",
            }}
          >
            {property.title}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#6B7280",
              marginBottom: "8px",
            }}
          >
            {property.location}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "8px",
              marginBottom: "10px",
              paddingBottom: "10px",
              borderBottom: "1px solid #E4E4E7",
            }}
          >
            <span
              style={{ fontSize: "16px", fontWeight: 800, color: "#F59E0B" }}
              data-testid="map-popup-price"
            >
              {formatPrice(property)}
            </span>
            {formatPricePerM2(property) && (
              <span style={{ fontSize: "10px", color: "#6B7280" }} data-testid="map-popup-ppm2">
                {formatPricePerM2(property)}
              </span>
            )}
          </div>

          {/* AI Section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "6px",
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #F59E0B, #D97706)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "10px",
              }}
            >
              ✦
            </div>
            <span
              style={{
                fontSize: "10px",
                textTransform: "uppercase",
                letterSpacing: "1px",
                color: "#6B7280",
                fontWeight: 700,
              }}
            >
              AI Değerlendirmesi
            </span>
          </div>

          {loading && (
            <div
              style={{
                fontSize: "11px",
                color: "#6B7280",
                fontStyle: "italic",
              }}
            >
              Analiz yapılıyor...
            </div>
          )}

          {!loading && !summary && (
            <div style={{ fontSize: "11px", color: "#6B7280" }}>
              Değerlendirme için bekleyin
            </div>
          )}

          {!loading && summary && verdict && (
            <>
              <div
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: "9999px",
                  backgroundColor: verdict.bg,
                  color: verdict.color,
                  fontSize: "10px",
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  marginBottom: "6px",
                }}
              >
                {verdict.label}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#0A0A0A",
                  marginBottom: "4px",
                  lineHeight: 1.3,
                }}
              >
                {summary.headline}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#374151",
                  lineHeight: 1.4,
                }}
              >
                {summary.summary}
              </div>
            </>
          )}
        </div>
      </Tooltip>
    </Marker>
  );
};

const MapView = ({ properties, onPropertyClick }) => {
  const [navProp, setNavProp] = useState(null);
  const [showAllTrigger, setShowAllTrigger] = useState(0);
  const defaultCenter = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem("emlak-last-map"));
      if (saved?.lat && saved?.lng) return [saved.lat, saved.lng];
    } catch (e) { console.debug("map center parse", e?.message); }
    return [39.9334, 32.8597];
  })();
  const defaultZoom = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem("emlak-last-map"));
      if (saved?.zoom) return saved.zoom;
    } catch (e) { console.debug("map zoom parse", e?.message); }
    return 6;
  })();
  const center = defaultCenter;

  const saveMapState = (map) => {
    try {
      const c = map.getCenter();
      localStorage.setItem("emlak-last-map", JSON.stringify({ lat: c.lat, lng: c.lng, zoom: map.getZoom() }));
    } catch (e) { console.debug("map state save", e?.message); }
  };

  return (
    <div data-testid={DASHBOARD.mapView} className="w-full h-full relative">
      <MapContainer
        center={center}
        zoom={defaultZoom}
        className="w-full h-full"
        zoomControl={false}
        whenReady={(e) => {
          const map = e.target || e;
          if (map?.on) {
            map.on("moveend", () => saveMapState(map));
            map.on("zoomend", () => saveMapState(map));
          }
          // Add zoom control at top-right so it never collides with the
          // mobile hamburger (top-left) or the desktop sidebar.
          try {
            if (map?.zoomControl) map.removeControl(map.zoomControl);
            if (window.L && window.L.control) {
              window.L.control.zoom({ position: "topright" }).addTo(map);
            }
          } catch (_e) { /* leaflet not loaded yet — ignore */ }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapUpdater properties={properties} showAllTrigger={showAllTrigger} />

        {properties.filter(p => p.lat && p.lng).map((property) => (
          <PropertyMarker
            key={property.id}
            property={property}
            onPropertyClick={onPropertyClick}
            onOpenNav={setNavProp}
          />
        ))}
      </MapContainer>

      {navProp && (
        <NavigationDialog
          open={!!navProp}
          onClose={() => setNavProp(null)}
          lat={navProp.lat}
          lng={navProp.lng}
          title={navProp.title}
        />
      )}

      {/* Show all properties button — bottom-left on mobile (clears
          the mobile bottom navigation), top-right area is reserved for
          the Leaflet zoom controls, top-left for the hamburger. */}
      {properties.some(p => p.lat && p.lng) && (
        <button
          data-testid="show-all-properties-button"
          onClick={() => setShowAllTrigger(t => t + 1)}
          className="absolute left-4 z-[1000] bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A] px-3 py-2 rounded-lg text-xs font-semibold text-[#0A0A0A] dark:text-[#FAFAFA] shadow-sm flex items-center gap-1.5 transition-colors bottom-24 lg:bottom-4"
          style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" />
          Tüm İlanları Göster
        </button>
      )}

      {/* Custom "Map data" badge removed — Leaflet's built-in attribution
          (bottom-right) already covers the OpenStreetMap licensing
          requirement, and the duplicate badge was overlapping the mobile
          bottom navigation. */}
    </div>
  );
};

export default MapView;
